import { Component, HostBinding } from '@angular/core'
import { Sort } from '@angular/material/sort'
import { Router } from '@angular/router'
import { AuthManager } from 'src/app/shared/auth/AuthManager'
import { ApplicationArea, EntraAddition, EntraDisable, EntraExclusion, EntraUnknown, EntraSyncClient } from 'src/app/shared/services/ppv.api'

//Run with \PPV.Web\ClientApp\ng serve
//Make sure PPV.API is running and are on the VPN

@Component({
   selector: 'entra-sync-app',
   templateUrl: './entra-sync-app.component.html',
   styleUrls: ['./entra-sync-app.component.scss'],
   standalone: false,
})
export class EntraSyncAppComponent {
   @HostBinding('class.full-flex-height') fullFlexHeight = true

   public additions: EntraAddition[] = []
   public disables: EntraDisable[] = []
   public unknowns: EntraUnknown[] = []
   public exclusions: EntraExclusion[] = []

   public isAdditionsLoaded = false
   public isDisablesLoaded = false
   public isUnknownsLoaded = false
   public isExclusionsLoaded = false

   selectedButton: string = 'created' // Track selected button
   setSelectedButton(button: string) {
      this.selectedButton = button
   }

   public unknownsToSave: { [key: string]: any } = {}
   public exclusionsToSave: { [key: string]: any } = {}

   actionOptions = [
      { label: 'Disable Account', value: 'Disable' },
      { label: 'Add Exclusion - Contractor', value: 'Contractor' },
      { label: 'Add Exclusion - Clinic', value: 'Clinic' },
      { label: 'Add Exclusion - Admin', value: 'Admin' },
      { label: 'Add Exclusion - Corporate', value: 'Corporate' },
      { label: 'Add Exclusion - Shared Mailbox', value: 'Shared Mailbox' },
   ]

   exclusionOptions = [
      { label: 'Contractor', value: 'Contractor' },
      { label: 'Clinic', value: 'Clinic' },
      { label: 'Admin', value: 'Admin' },
      { label: 'Corporate', value: 'Corporate' },
      { label: 'Shared Mailbox', value: 'Shared Mailbox' },
      { label: 'Remove From Exclusions', value: 'Remove From Exclusions' },
   ]

   constructor(private authMgr: AuthManager, private router: Router, private entraSyncClient: EntraSyncClient) {
      this.authMgr.getCurrentUser().subscribe((user) => {
         this.getEntraAdditions()

         this.getEntraDisables()

         this.getEntraUnknowns()

         this.getEntraExclusions()
      })
   }

   getActiveExclusion(exclusion: EntraExclusion): string {
      if (exclusion.contractorKeepActive) return 'Contractor'
      if (exclusion.clinicKeepActive) return 'Clinic'
      if (exclusion.adminKeepActive) return 'Admin'
      if (exclusion.corporateKeepActive) return 'Corporate'
      if (exclusion.sharedMailboxKeepActive) return 'Shared Mailbox'
      return '' // Default case
   }

   public getEntraAdditions() {
      this.entraSyncClient.getAdditions().subscribe({
         next: (data) => {
            console.log(data)
            this.additions = data
            this.isAdditionsLoaded = true
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   public getEntraDisables() {
      this.entraSyncClient.getDisables().subscribe({
         next: (data) => {
            console.log(data)

            //save the data, but map the KeepActive cols to a string for the dropdown
            this.disables = data.map((disable) => ({
               ...disable,
               displayName: disable.firstName + ' ' + disable.lastName,
            }))

            this.isDisablesLoaded = true
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   public getEntraUnknowns() {
      this.entraSyncClient.getUnknowns().subscribe({
         next: (data) => {
            console.log(data)
            this.unknowns = data
            this.isUnknownsLoaded = true
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   public getEntraExclusions() {
      this.entraSyncClient.getExclusions().subscribe({
         next: (data) => {
            console.log(data)

            //save the data, but map the KeepActive cols to a string for the dropdown
            this.exclusions = data.map((exclusion) => ({
               ...exclusion,
               selectedExclusion: this.getActiveExclusion(exclusion),
            }))

            this.isExclusionsLoaded = true
         },
         error: (error) => {
            console.error('Error fetching batch data:', error)
         },
      })
   }

   onClickSave(): void {
      console.log('CLICKED SAVE')
   }

   openEntraProfile(upn: string) {
      console.log('Opening Entra profile for: ' + upn)

      this.entraSyncClient.getEntraLink(upn).subscribe({
         next: (data) => {
            console.log(data)

            if (data) {
               //open the url in a new tab
               window.open(data, '_blank')
            } else {
               console.error('No URL received.')
            }
         },
         error: (error) => {
            console.error('Error fetching URL:', error)
         },
      })
   }

   openMicrosoft365Profile(upn: string) {
      console.log('Opening Microsoft365 profile for: ' + upn)

      this.entraSyncClient.getMicrosoft365Link(upn).subscribe({
         next: (data) => {
            console.log(data)

            if (data) {
               //open the url in a new tab
               window.open(data, '_blank')
            } else {
               console.error('No URL received.')
            }
         },
         error: (error) => {
            console.error('Error fetching URL:', error)
         },
      })
   }

   currentUPN: string = ''
   editUPN(upn: string) {
      this.isEditingMappingUPN = true
      this.currentUPN = upn
      console.log('Editing: ' + upn)
   }

   updatedUPN: string = ''
   isSavingUPN = false
   saveUpdatedUPN() {
      console.log('Updating UPN:', this.updatedUPN)
      this.isSavingUPN = true

      this.validateUPN()

      if (this.isUPNValid) {
         //call the api with upn update
         this.entraSyncClient.updateMappingUPN(this.currentUPN, this.updatedUPN).subscribe({
            next: (data) => {
               console.log('Updated UPN in DayforceEmployeeMeta: ' + data)

               //update the addition in the view model
               var additionToUpdate: EntraAddition = this.additions.find((addn) => addn.entraUPN === this.currentUPN)
               additionToUpdate.entraUPN = this.updatedUPN

               this.currentUPN = this.updatedUPN
            },
            error: () => {
               console.error('ERROR: Database Error saving UPN.')
            },
         })
      } else {
         console.log('ERROR: Invalid UPN, could not save.')
      }
   }

   isUPNValid: boolean = false
   validateUPN() {
      const upnRegex = /^[a-zA-Z0-9._-]+@vetcor\.com$/
      this.isUPNValid = upnRegex.test(this.updatedUPN)
   }

   isEditingMappingUPN = false
   closePopup(): void {
      this.isEditingMappingUPN = false
      this.isSavingUPN = false
      this.updatedUPN = ''
   }

   handleUnknownAccountAction(event: any, unknown: EntraUnknown) {
      console.log('Selected action:', event.value)
      console.log('User:', unknown.userPrincipalName)
      console.log(unknown)

      if (event.value === 'Disable') {
         unknown['toDisable'] = true
      } else {
         unknown['toDisable'] = false

         //generate a EntraExclusion to save
         var exclusionToSave: EntraExclusion = {
            userPrincipalName: unknown.userPrincipalName,
            entraDisplayName: unknown.displayName,
            contractorKeepActive: event.value === 'Contractor',
            clinicKeepActive: event.value === 'Clinic',
            adminKeepActive: event.value === 'Admin',
            corporateKeepActive: event.value === 'Corporate',
            sharedMailboxKeepActive: event.value === 'Shared Mailbox',
         }

         unknown['exclusion'] = exclusionToSave
      }

      //record the change and shows save button
      this.unknownsToSave[unknown.userPrincipalName] = unknown
   }

   saveUnknownAccountAction(unknown: any) {
      console.log('Saving')
      console.log(unknown)

      if (unknown.toDisable) {
         console.log('Disabling unknown account for ' + unknown.userPrincipalName)

         this.entraSyncClient.disableAccount(unknown.userPrincipalName).subscribe({
            next: (data) => {
               console.log('Disabled unknown account: ' + data)

               //hide the save button
               delete this.unknownsToSave[unknown.userPrincipalName]

               //remove the unknown from the view model
               this.unknowns = this.unknowns.filter((unk) => unk.userPrincipalName !== unknown.userPrincipalName)
            },
            error: () => {
               console.error('ERROR: Could not remove unknown from the database.')

               //TODO: show error in a toast
            },
         })
      } else {
         console.log('Adding unknown to exclusions:')
         console.log(unknown)

         //make sure there is an unknown.exclusion to save
         if (!unknown.exclusion) {
            console.error('ERROR: There was no unknown exclusion to save to the database.')
            return
         }

         //add the unknown.exclusion to the db
         this.entraSyncClient.addExclusion(unknown.exclusion).subscribe({
            next: (data) => {
               console.log('Added unknown exclusion: ' + data)

               //hide the save button
               delete this.unknownsToSave[unknown.userPrincipalName]

               //remove the unknown from the view model
               this.unknowns = this.unknowns.filter((unk) => unk.userPrincipalName !== unknown.userPrincipalName)

               //add the unknown.exclusion to the exclusions view model so we don't need to refresh
               unknown.exclusion.selectedExclusion = this.getActiveExclusion(unknown.exclusion)

               this.exclusions.push(unknown.exclusion)
            },
            error: () => {
               console.error('ERROR: Could not save unknown exclusion updates to the database.')

               //TODO: show error in a toast
            },
         })
      }
   }

   handleExclusionChange(event: any, exclusion: EntraExclusion) {
      console.log('Selected action:', event.value)

      if (event.value === 'Remove From Exclusions') {
         exclusion['toRemove'] = true
      } else {
         exclusion['toRemove'] = false

         //save the exclusion type
         exclusion.adminKeepActive = event.value === 'Admin'
         exclusion.clinicKeepActive = event.value === 'Clinic'
         exclusion.contractorKeepActive = event.value === 'Contractor'
         exclusion.corporateKeepActive = event.value === 'Corporate'
         exclusion.sharedMailboxKeepActive = event.value === 'Shared Mailbox'
      }

      console.log('User:', exclusion.userPrincipalName)
      console.log(exclusion)

      //record the change and shows save button
      this.exclusionsToSave[exclusion.userPrincipalName] = exclusion
   }

   saveExclusionChange(exclusion: any) {
      if (exclusion.toRemove) {
         console.log('Removing exclusion for ' + exclusion.userPrincipalName)

         //make sure it's not marked to be removed anymore
         exclusion.toRemove = false

         this.entraSyncClient.removeExclusion(exclusion.userPrincipalName).subscribe({
            next: (data) => {
               console.log('REMOVED exclusion: ' + data)

               //hide the save button
               delete this.exclusionsToSave[exclusion.userPrincipalName]

               //remove the exclusion from the view model
               this.exclusions = this.exclusions.filter((excl) => excl.userPrincipalName !== exclusion.userPrincipalName)
            },
            error: () => {
               console.error('ERROR: Could not remove exclusion from the database.')

               //TODO: show error in a toast
            },
         })
      } else {
         console.log('Saving exclusion:')
         console.log(exclusion)
         //save the exclusion changes to the db
         this.entraSyncClient.saveExclusion(exclusion).subscribe({
            next: (data) => {
               console.log('SAVED exclusion: ' + data)

               delete this.exclusionsToSave[exclusion.userPrincipalName]
            },
            error: () => {
               console.error('ERROR: Could not save exclusion updates to the database.')

               //TODO: show error in a toast
            },
         })
      }
   }
}
